import React, { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { AxiosResponse } from 'axios';
import { Navigate } from 'react-router-dom';
import { TafButton } from '@taf-ui/react';

import AppStorage from '../Storage/Storage';
import { isBrand, translate } from '../utils/utils';
import Requests from '../Requests/Requests';
import RouteParams from '../interfaces/RouteParams';
import Labels from '../enums/Labels';
import Brand from '../enums/Brand';

import './WaitForSigning.css';

import { languageToReadSpeakerLang } from 'ReadSpeakerReader/ReadSpeakerProvider';
import ReadSpeakerReader from 'ReadSpeakerReader';

type WaitForSigningProps = {
  initWholeAppFn?: () => void;
  navigate?: () => void;
} & RouteParams;

interface WaitForSigningState {
  navigateComponent?: ReactNode;
}

class WaitForSigning extends React.PureComponent<
  WaitForSigningProps,
  WaitForSigningState
> {
  private readonly storage: AppStorage;

  private readonly headerLabel: Labels;

  private readonly p1Label: Labels;

  private readonly p2Label: Labels;

  private readonly signingBtnLabel: Labels;

  constructor(props: WaitForSigningProps) {
    super(props);
    this.storage = AppStorage.getInstance();
    this.state = {
      navigateComponent: undefined,
    };
    if (isBrand(Brand.wefox)) {
      this.headerLabel = Labels.WAITING_FOR_SIGNING_WEFOX_HEADER;
      this.p1Label = Labels.WAITING_FOR_SIGNING_WEFOX_P1;
      this.p2Label = Labels.WAITING_FOR_SIGNING_WEFOX_P2;
      this.signingBtnLabel = Labels.PROCEED_TO_SIGNING_WEFOX;
    } else {
      this.headerLabel = Labels.WAITING_FOR_SIGNING_HEADER;
      this.p1Label = Labels.WAITING_FOR_SIGNING_P1;
      this.p2Label = Labels.WAITING_FOR_SIGNING_P2;
      this.signingBtnLabel = Labels.PROCEED_TO_SIGNING;
    }
  }

  handleReopenHandler = (response: AxiosResponse): void => {
    const { status } = response;

    if (status === 200) {
      this.redirectToInstructionsPage();
    }
  };

  openSignhost(): void {
    const signUrl = this.storage.getSignUrl;
    if (signUrl) {
      window.location.href = signUrl;
    }
  }

  redirectToInstructionsPage(): void {
    const { healthDeclarationId, initWholeAppFn } = this.props;

    if (initWholeAppFn) {
      initWholeAppFn();
    }

    this.storage.setFieldsWhenReopenHD();
    this.setState({
      navigateComponent: (
        <Navigate to={`/${healthDeclarationId}/instructions`} replace={true} />
      ),
    });
  }

  reopen(): void {
    void Requests.reopenHealthDeclaration().then(this.handleReopenHandler);
  }

  render(): React.ReactElement {
    const lang = this.storage.getLocale;
    const { navigateComponent } = this.state;

    if (navigateComponent) {
      return <>{navigateComponent}</>;
    }

    return (
      <div
        className="waiting-screen"
        lang={languageToReadSpeakerLang(lang)}
        id="wait-for-signing"
      >
        <ReadSpeakerReader id="wait-for-signing" />
        <h1>{translate(this.headerLabel, lang)}</h1>
        <p className="info-message">{translate(this.p1Label, lang)}</p>
        <p className="info-message">{translate(this.p2Label, lang)}</p>
        <div className="buttons-wrapper">
          <TafButton
            data-testid="openSignHost"
            id="openSignHost"
            onClick={this.openSignhost.bind(this)}
            type="button"
            primary={true}
            title={translate(this.signingBtnLabel, lang)}
          >
            {translate(this.signingBtnLabel, lang)}
          </TafButton>
          <TafButton
            id="reopenBtn"
            onClick={this.reopen.bind(this)}
            type="button"
            title={translate(Labels.EDIT_HEALTH_DECLARATION, lang)}
          >
            {translate(Labels.EDIT_HEALTH_DECLARATION, lang)}
          </TafButton>
        </div>
      </div>
    );
  }
}

export default observer(WaitForSigning);
